/**
 * Order Gauge Options Service
 *
 * @version
 * 4.71.0 OAUI-693 Tidy and remove duplicated code
 * 4.71.0 OAUI-684 Improve shared code and add interfaces
 * 4.61.0 OAUI-643 Add material and gauge fields for adjustments
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2022-2024 Build Centrix
 */
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { WdLocalSharedFunctionsService } from '../../../shared/wd-local-shared-functions.service';
import { IGaugeOption } from '../../../../interfaces/orders/order-interfaces';

@Injectable({
  providedIn: 'root',
})
export class WdOrderGaugeOptionsService {
  // Booleans
  isLoading = new BehaviorSubject(false);

  // Options
  gaugeOptions = new BehaviorSubject<Array<IGaugeOption>>([]);

  /**
   * Constructor
   *
   * @since 4.61.0
   */
  constructor(
    private translate: TranslateService,
    private wdLocalSharedFunctions: WdLocalSharedFunctionsService,
    @Inject('wdErrorService') public wdErrorService: any,
    @Inject('wdOauth2HttpService') public wdOauth2HttpService: any,
    @Inject('wdSharedFunctionsService') public wdSharedFunctionsService: any,
  ) {}

  /**
   * Parse Single Gauge
   * @since 4.61.0
   */
  static parseGauge(gauge: any): object {
    return {
      value: gauge.value,
    };
  }

  /**
   * Sort Gauges
   * @since 4.61.0
   */
  static sortGauges(gauges: any): Array<any> {
    return gauges.sort((a: any, b: any) => a.value - b.value);
  }

  /**
   * GET gauge options
   *
   * @since 4.61.0
   * @memberof WdOrderGaugeOptionsService
   */
  async getGaugeOptions(): Promise<any> {
    // Toggle Loader
    this.isLoading.next(true);
    try {
      // Success
      const response = await this.wdOauth2HttpService.get(
        `${this.wdLocalSharedFunctions.publicApiUrl}materials/gauges?responseFormat=bcxResult`,
      );
      const options: any = this.parseGauges(response.data);
      this.gaugeOptions.next(options);
      return this.gaugeOptions.value;
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.translate.instant('labels.error'),
        error.statusText,
      );
      throw error;
    } finally {
      // Toggle Loader
      this.isLoading.next(false);
    }
  }

  /**
   * Parse Multiple Gauges
   * @since 4.61.0
   * @param gauges
   */
  parseGauges(gauges: any): Array<any> {
    const gaugesParsed = gauges.map((gauge: any) =>
      WdOrderGaugeOptionsService.parseGauge(gauge),
    );
    return WdOrderGaugeOptionsService.sortGauges(gaugesParsed);
  }
}
